import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    pages: 0,
    announcements: 0,
    events: 0,
    contacts: 0,
    recentActivity: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [pagesRes, announcementsRes, eventsRes, contactStatsRes] = await Promise.all([
          apiService.getPages(),
          apiService.getAnnouncements({ limit: 1 }),
          apiService.getEvents({ limit: 1 }),
          apiService.getContactStats()
        ]);

        setStats({
          pages: pagesRes.data?.pages?.length || 0,
          announcements: announcementsRes.data?.pagination?.totalItems || 0,
          events: eventsRes.data?.pagination?.totalItems || 0,
          contacts: contactStatsRes.data?.overview?.total || 0,
          recentActivity: []
        });
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  const quickActions = [
    {
      title: 'Create Announcement',
      description: 'Add a new announcement',
      icon: 'fas fa-bullhorn',
      link: '/admin/announcements/new',
      color: 'bg-crimson'
    },
    {
      title: 'Create Event',
      description: 'Schedule a new event',
      icon: 'fas fa-calendar-plus',
      link: '/admin/events/new',
      color: 'bg-slate'
    },
    {
      title: 'Manage Pages',
      description: 'Edit website pages',
      icon: 'fas fa-edit',
      link: '/admin/pages',
      color: 'bg-crimson'
    },
    {
      title: 'View Contacts',
      description: 'Check contact messages',
      icon: 'fas fa-envelope-open',
      link: '/admin/contacts',
      color: 'bg-slate'
    }
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-slate font-sans">
          <i className="fas fa-spinner fa-spin mr-2 text-crimson"></i>
          Loading dashboard...
        </div>
      </div>
    );
  }

  return (
    <div className="font-sans">
      <div className="mb-8">
        <h1 className="text-3xl font-sans font-bold text-slate mb-2">
          Welcome back, {user?.username}!
        </h1>
        <p className="text-gray-600 font-medium">
          Here's what's happening with your website today.
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-crimson rounded-lg flex items-center justify-center">
                <i className="fas fa-file-alt text-white"></i>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Pages</p>
              <p className="text-2xl font-bold text-slate font-sans">{stats.pages}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-slate rounded-lg flex items-center justify-center">
                <i className="fas fa-bullhorn text-white"></i>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Announcements</p>
              <p className="text-2xl font-bold text-slate font-sans">{stats.announcements}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-crimson rounded-lg flex items-center justify-center">
                <i className="fas fa-calendar text-white"></i>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Events</p>
              <p className="text-2xl font-bold text-slate font-sans">{stats.events}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg border border-border_gray shadow-sm p-6 hover:shadow-md transition-shadow">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-slate rounded-lg flex items-center justify-center">
                <i className="fas fa-envelope text-white"></i>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Contacts</p>
              <p className="text-2xl font-bold text-slate font-sans">{stats.contacts}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mb-8">
        <h2 className="text-xl font-sans font-semibold text-slate mb-4">Quick Actions</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {quickActions.map((action, index) => (
            <Link
              key={index}
              to={action.link}
              className="block p-4 bg-white rounded-lg border border-border_gray hover:shadow-md hover:border-crimson transition-all duration-200"
            >
              <div className="flex items-center">
                <div className={`w-10 h-10 ${action.color} rounded-lg flex items-center justify-center`}>
                  <i className={`${action.icon} text-white`}></i>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-slate">{action.title}</h3>
                  <p className="text-xs text-gray-600">{action.description}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="bg-white rounded-lg border border-border_gray shadow-sm">
        <div className="px-6 py-4 border-b border-border_gray">
          <h3 className="text-lg font-sans font-semibold text-slate">Recent Activity</h3>
        </div>
        <div className="p-6">
          {stats.recentActivity.length > 0 ? (
            <ul className="space-y-3">
              {stats.recentActivity.map((activity) => (
                <li key={activity.id} className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-2 h-2 bg-crimson rounded-full"></div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-slate font-medium">{activity.title}</p>
                    <p className="text-xs text-gray-600">
                      {new Date(activity.timestamp).toLocaleDateString()}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-center py-8">
              <i className="fas fa-clock text-4xl text-gray-300 mb-2"></i>
              <p className="text-gray-600">No recent activity to show.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;